import api from '@/services/api';
import { ApiResponse, FcmTokenRow } from '@/services/types';
import { API_ENDPOINTS, STORAGE_KEYS } from '@/utils/constants';

/**
 * Web push via Firebase Cloud Messaging.
 *
 * Inert unless VITE_FIREBASE_* env vars are set: the Firebase project currently has only an
 * Android app registered, so a Web App + VAPID key must be created in the console first.
 * Foreground messages are re-broadcast as a DOM event so screens can react (e.g. paid invoice).
 */
export interface PushPayload { title?: string; body?: string; target?: string; targetId?: string }

export const PUSH_EVENT = 'pos:push';

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
const vapidKey: string | undefined = import.meta.env.VITE_FIREBASE_VAPID_KEY;

export const isPushConfigured = () => Boolean(firebaseConfig.apiKey && firebaseConfig.appId && vapidKey);

let listening = false;

export async function registerPush(shopId: number) {
    if (!isPushConfigured() || !('serviceWorker' in navigator) || !('Notification' in window)) return;
    try {
        if (Notification.permission === 'default') await Notification.requestPermission();
        if (Notification.permission !== 'granted') return;

        const { initializeApp, getApps } = await import('firebase/app');
        const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
        const app = getApps()[0] ?? initializeApp(firebaseConfig);
        const messaging = getMessaging(app);

        const swUrl = '/firebase-messaging-sw.js?' + new URLSearchParams(firebaseConfig as Record<string, string>).toString();
        const swReg = await navigator.serviceWorker.register(swUrl);
        const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swReg });
        if (!token) return;

        const res = await api.post<ApiResponse<FcmTokenRow>>(API_ENDPOINTS.FCM_TOKEN(shopId), null, {
            params: { firebaseId: token },
        });
        localStorage.setItem(STORAGE_KEYS.FCM_ROW_ID, String(res.data.data.id));

        if (!listening) {
            listening = true;
            onMessage(messaging, (msg) => {
                const detail: PushPayload = {
                    title: msg.notification?.title,
                    body: msg.notification?.body,
                    target: msg.data?.target,
                    targetId: msg.data?.targetId,
                };
                window.dispatchEvent(new CustomEvent<PushPayload>(PUSH_EVENT, { detail }));
            });
        }
    } catch (e) {
        console.warn('[push] register failed', e);
    }
}

export async function unregisterPush(shopId: number) {
    const rowId = localStorage.getItem(STORAGE_KEYS.FCM_ROW_ID);
    if (!rowId) return;
    try {
        // Backend expects the numeric row id (not the token string) in `firebaseId`.
        await api.post(API_ENDPOINTS.FCM_TOKEN_DELETE(shopId), null, { params: { firebaseId: rowId } });
    } catch (e) {
        console.warn('[push] unregister failed', e);
    } finally {
        localStorage.removeItem(STORAGE_KEYS.FCM_ROW_ID);
    }
}

/** Subscribe to foreground pushes. Returns an unsubscribe. */
export function onPush(cb: (p: PushPayload) => void) {
    const handler = (e: Event) => cb((e as CustomEvent<PushPayload>).detail);
    window.addEventListener(PUSH_EVENT, handler);
    return () => window.removeEventListener(PUSH_EVENT, handler);
}
